<template>
  <div class="back-office w-100 p-3 bg-white" style="min-height: 100vh; flex: 1;">
    <!-- Header -->
    <div class="d-flex align-items-center mb-3">
      <div>
        <div class="font-size-18 fw-bold">Back Office — Liên kết tài khoản</div>
        <div class="font-size-13 text-grey600 mt-1">Tra cứu danh sách tài khoản Zalo theo CompanyCode hoặc CompanyOwner</div>
      </div>
    </div>

    <!-- Filter row -->
    <div class="bo-filter-card d-flex align-items-center gap-2 p-3 mb-3">
      <el-input
        v-model="filterCompanyCode"
        placeholder="CompanyCode"
        clearable
        size="medium"
        class="me-2"
        style="flex: 1; max-width: 280px;"
        @keyup.enter.native="check" />
      <el-input
        v-model="filterCompanyAlias"
        placeholder="CompanyOwner"
        clearable
        size="medium"
        class="me-2"
        style="flex: 1; max-width: 280px;"
        @keyup.enter.native="check" />
      <el-button type="primary" size="medium" :loading="loading" @click="check">Kiểm tra</el-button>
    </div>

    <!-- Counter boxes -->
    <div class="row mb-3">
      <div class="col-md-4 mb-2">
        <div class="bo-stat-card bo-stat-active">
          <div class="bo-stat-label">Đang hoạt động</div>
          <div class="bo-stat-value">{{ stats.active }}</div>
        </div>
      </div>
      <div class="col-md-4 mb-2">
        <div class="bo-stat-card bo-stat-off">
          <div class="bo-stat-label">Cần đăng nhập lại</div>
          <div class="bo-stat-value">{{ stats.off }}</div>
        </div>
      </div>
      <div class="col-md-4 mb-2">
        <div class="bo-stat-card bo-stat-deleted">
          <div class="bo-stat-label">Đã xoá</div>
          <div class="bo-stat-value">{{ stats.deleted }}</div>
        </div>
      </div>
    </div>

    <!-- Account list -->
    <div class="bo-list-card">
      <div class="d-flex p-3 align-items-center gap-2 flex-wrap" style="border-bottom: 1px solid #EEE;">
        <el-image src="/assets/icon/icon_zalo.png" style="width: 28px; height: 28px;" />
        <div class="fw-bold font-size-16 ms-2 me-3">Danh sách tài khoản</div>
        <div class="flex-1" />
        <el-select
          v-model="statusFilter"
          size="small"
          placeholder="Trạng thái"
          style="width: 200px;">
          <el-option label="Tất cả trạng thái" value="" />
          <el-option label="Đang hoạt động" value="ACTIVE" />
          <el-option label="Cần đăng nhập lại" value="OFF" />
        </el-select>
        <el-input
          v-model="searchName"
          size="small"
          placeholder="Tìm theo tên / SĐT / ID"
          clearable
          prefix-icon="el-icon-search"
          class="ms-2"
          style="width: 260px;" />
      </div>

      <div v-if="filteredAccounts.length > 0" class="d-flex p-3 fw-bold font-size-14 bo-list-header">
        <div style="width: 60px;" class="text-center">STT</div>
        <div class="bo-col-name">Tên đăng nhập</div>
        <div class="bo-col-md text-center">Đăng nhập gần nhất</div>
        <div class="bo-col-md text-center">Duy trì kết nối</div>
        <div class="bo-col-md text-center">Trạng thái</div>
      </div>

      <div v-if="filteredAccounts.length > 0">
        <div v-for="(account, index) in filteredAccounts" :key="account.accountId" class="d-flex p-3 align-items-center bo-list-row">
          <div style="width: 60px;" class="text-center fw-semibold font-size-16">{{ index + 1 }}</div>
          <div class="bo-col-name">
            <div class="d-flex align-items-center gap-2">
              <el-avatar :src="account.avatar" />
              <div>
                <div class="fw-bold">{{ account.displayName }}</div>
                <div class="font-size-13 text-danger" v-if="account.swName && account.swName.length > 0">
                  Biệt danh : {{ account.swName }}
                </div>
                <div class="font-size-13" v-if="account.phoneNumber && account.phoneNumber.length > 0">
                  Số điện thoại : {{ account.phoneNumber }}
                </div>
                <div class="d-flex text-primary font-size-13">
                  <div>ID : {{ account.accountId }}</div>
                  <el-tooltip class="item mx-1" effect="dark" content="Sao chép" placement="top">
                    <i class="pointer el-icon-copy-document mt-1" @click="copyToClipboard(account.accountId, 'Sao chép ID thành công !')" />
                  </el-tooltip>
                </div>
                <div class="font-size-13 text-grey600">Cập nhật lúc : {{ convertTime(account.updateAt) }}</div>
              </div>
            </div>
          </div>
          <div class="bo-col-md text-center font-size-13">{{ formatLoginAt(account.loginAt) }}</div>
          <div class="bo-col-md text-center">
            <el-switch :value="!!account.autoLogin" disabled />
          </div>
          <div class="bo-col-md text-center" :class="account.status === 'ACTIVE' ? 'text-success' : 'text-danger'">
            <span class="font-size-13 fw-semibold">
              {{ account.status === 'ACTIVE' ? 'Đang hoạt động' : 'Cần đăng nhập lại' }}
            </span>
          </div>
        </div>
      </div>

      <div v-else class="p-4 text-center text-grey600 font-size-14">
        <template v-if="!checked">Nhập CompanyCode hoặc CompanyOwner rồi bấm Kiểm tra</template>
        <template v-else-if="accounts.length === 0">Không có dữ liệu</template>
        <template v-else>Không có tài khoản nào khớp bộ lọc</template>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";

export default {
  name: "BackOffice",
  data() {
    return {
      filterCompanyCode: "",
      filterCompanyAlias: "",
      loading: false,
      checked: false,
      accounts: [],
      stats: {
        active: 0,
        off: 0,
        deleted: 0
      },
      statusFilter: "",
      searchName: ""
    };
  },
  computed: {
    filteredAccounts() {
      const status = (this.statusFilter || "").trim();
      const kw = (this.searchName || "").trim().toLowerCase();
      return this.accounts.filter((a) => {
        if (status && a.status !== status) return false;
        if (kw) {
          const hay = [
            a.displayName,
            a.zaloName,
            a.swName,
            a.phoneNumber,
            a.accountId,
            a.username
          ]
            .filter((v) => v != null)
            .map((v) => String(v).toLowerCase())
            .join(" ");
          if (!hay.includes(kw)) return false;
        }
        return true;
      });
    }
  },
  methods: {
    formatLoginAt(ts) {
      if (!ts || ts <= 0) return "—";
      return moment(Number(ts)).format("YYYY-MM-DD HH:mm:ss");
    },
    async check() {
      const code = (this.filterCompanyCode || "").trim();
      const alias = (this.filterCompanyAlias || "").trim();
      if (!code && !alias) {
        return this.alert("error", "Vui lòng nhập CompanyCode hoặc CompanyOwner.");
      }
      this.loading = true;
      try {
        const params = {};
        if (code) params.companyCode = code;
        if (alias) params.companyAlias = alias;
        const resp = await this.$http.get("/api/account/backOffice", { params });
        const data = (resp && resp.data) || {};
        this.accounts = Array.isArray(data.accounts) ? data.accounts : [];
        this.stats = {
          active: Number(data.activeNum) || 0,
          off: Number(data.offNum) || 0,
          deleted: Number(data.deletedNum) || 0
        };
        this.checked = true;
      } catch (e) {
        const msg = e && e.error && e.error.message ? e.error.message : "Có lỗi xảy ra";
        this.alert("error", msg);
        this.accounts = [];
        this.stats = { active: 0, off: 0, deleted: 0 };
        this.checked = true;
      } finally {
        this.loading = false;
      }
    },
    copyToClipboard(text, message) {
      navigator.clipboard && navigator.clipboard.writeText(text);
      this.alert("success", message);
    }
  },
  created() {
    // Guard: chỉ companyCode === 'sota' mới được vào.
    if (!this.app || !this.app.user || !["sota", "esbt"].includes(this.app.user.companyCode)) {
      this.$router.replace("/");
    }
  }
};
</script>

<style scoped>
.back-office {
  background: #FAFAFA !important;
}
.bo-filter-card {
  background: #FFFFFF;
  border: 1px solid #DCDFE9;
  border-radius: 6px;
}
.bo-stat-card {
  padding: 16px 20px;
  border-radius: 8px;
  background: #FFFFFF;
  border: 1px solid #DCDFE9;
  border-left-width: 4px;
}
.bo-stat-active {
  border-left-color: #15A85F;
}
.bo-stat-off {
  border-left-color: #DB342E;
}
.bo-stat-deleted {
  border-left-color: #909399;
}
.bo-stat-label {
  font-size: 13px;
  color: #696B72;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.bo-stat-value {
  font-size: 28px;
  font-weight: 700;
  color: #081B3A;
  margin-top: 6px;
}
.bo-list-card {
  background: #FFFFFF;
  border: 1px solid #DCDFE9;
  border-radius: 6px;
}
.bo-list-header {
  background: #F4F6FA;
  color: #696B72;
}
.bo-list-row {
  border-bottom: 1px solid #F0F2F5;
}
.bo-list-row:last-child {
  border-bottom: none;
}
.bo-col-name {
  flex: 2;
  min-width: 0;
}
.bo-col-md {
  flex: 1;
  min-width: 0;
}
</style>
